import { createContext, useContext, useState, useLayoutEffect, useMemo, useCallback } from 'react';

/**
 * Light/dark theme provider — explicit user control only, no "system" mode.
 *
 * "System" mode was removed because it caused a flash-of-wrong-theme race:
 * the React component tree painted before the prefers-color-scheme media
 * query was evaluated, leading to a half-rendered state where the page
 * background was light but child components carrying `dark:` Tailwind
 * variants rendered dark (and vice-versa). The `<html>` class is now
 * synchronously bootstrapped in `index.html` BEFORE the first paint —
 * see the inline <script> there. This provider reads that initial class
 * to stay in sync and persists user changes back to localStorage.
 */
const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

/**
 * Read whichever theme class the index.html bootstrap script already set.
 * Falls back to 'light' if neither class is present (SSR or test envs).
 */
function getInitialMode() {
  if (typeof document === 'undefined') return 'light';
  if (document.documentElement.classList.contains('dark')) return 'dark';
  return 'light';
}

const STORAGE_KEY = 'fp_theme_mode';

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  // useLayoutEffect (not useEffect) runs synchronously after DOM mutations
  // but BEFORE the browser paints, so swapping the theme via the in-app
  // toggle doesn't briefly show the old theme. Critical for the dark/light
  // toggle button feeling instantaneous.
  useLayoutEffect(() => {
    const el = document.documentElement;
    el.classList.remove('light', 'dark');
    el.classList.add(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* private mode etc. */ }
  }, [mode]);

  const setTheme = useCallback((m) => {
    // Guard against any old 'system' value still in callers. Anything that
    // isn't 'dark' falls back to 'light' rather than crashing.
    setMode(m === 'dark' ? 'dark' : 'light');
  }, []);
  const toggleTheme = useCallback(() => setMode(m => m === 'dark' ? 'light' : 'dark'), []);

  // `theme` = resolved theme (kept for backwards-compat with components
  // that read it). `mode` is identical now that 'system' is gone.
  const value = useMemo(
    () => ({ theme: mode, mode, setTheme, toggleTheme }),
    [mode, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
